// src/redux/slices/CustomFieldsSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosClient from "../../axios-client";

// ──────────────────────────────────────────────────────────────
//  ASYNC THUNKS
// ──────────────────────────────────────────────────────────────
export const AddCustomFields = createAsyncThunk(
  "customFields/addCustomFields",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        "category/addCategoryCustomLink",
        data,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const GetCustomFields = createAsyncThunk(
  "customFields/getCustomFields",
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        "category/listCategoryCustomLink",
        {
          params: { categoryId, pageSize: 100, pageNumber: 1 },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const deleteField = createAsyncThunk(
  "customFields/deleteCustomFields",
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.delete(
        `category/deleteCategoryCustomLink/${id}`,
        { params },
      );
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue({ id, error: error.response?.data });
    }
  },
);

export const updateField = createAsyncThunk(
  "customFields/updateCustomFields",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(
        `category/updateCategoryCustomLink/${id}`,
        data,
      );
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue({
        id,
        error: error.response?.data,
        oldData: data,
      });
    }
  },
);

// ──────────────────────────────────────────────────────────────
//  INITIAL STATE
// ──────────────────────────────────────────────────────────────
const initialState = {
  // Loading & Data
  catCustomFields: [],
  loadingCustomFields: false,
  errorCustomFields: null,
  totalQuestions: 0,

  // UI State (now global)
  selectedFilters: {}, // { [fieldId]: { [optionValue]: true } }
  openQuestions: {}, // { [fieldId]: true }

  // Async status
  addingField: false,
  updatingField: false,
  errorAddingField: null,
  errorUpdatingField: null,
  addedField: null,
};

// ──────────────────────────────────────────────────────────────
//  SLICE
// ──────────────────────────────────────────────────────────────
const CustomFieldsSlice = createSlice({
  name: "customFields",
  initialState,
  reducers: {
    // Legacy
    setCatcustomFields: (state, action) => {
      state.catCustomFields = action.payload;
    },

    // Full reset — clears everything
    clearBusinessFilters: () => initialState,
    // Add this inside your reducers object
    clearFilterSelectionsOnly: (state) => {
      state.selectedFilters = {}; // ← Unchecks all checkboxes
      state.openQuestions = {}; // ← Closes all dropdowns
    },
    // Toggle accordion open/close
    // CORRECT — Toggle independently, keep others open
    toggleQuestionOpen: (state, action) => {
      const fieldId = action.payload;
 
      state.openQuestions[fieldId] = true;
    },

    // Toggle checkbox option
    toggleFilterOption: (state, action) => {
      const { fieldId, optionValue } = action.payload;
      const field = state.selectedFilters[fieldId] || {};

      if (field[optionValue]) {
        delete field[optionValue];
      } else {
        field[optionValue] = true;
      }

      if (Object.keys(field).length === 0) {
        delete state.selectedFilters[fieldId];
      } else {
        state.selectedFilters[fieldId] = { ...field };
      }

      // Optional: auto-open when selecting
      state.openQuestions[fieldId] = true;
    },

    // Clear only selections (keep fields loaded)
    clearFilterSelections: (state) => {
      state.selectedFilters = {};
      state.openQuestions = {};
    },

    // Restore from saved string (e.g. from URL or Redux)
    restoreFilterSelections: (state, action) => {
      const { fields, savedString } = action.payload;
      const saved = savedString
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      const map = {};

      fields.forEach((field) => {
        if (!field.customValue) return;
        let options = [];
        try {
          const parsed = JSON.parse(field.customValue);
          options = Array.isArray(parsed) ? parsed : [];
        } catch {}

        const fieldMap = {};
        saved.forEach((val) => {
          if (options.includes(val)) {
            fieldMap[val] = true;
          }
        });
        if (Object.keys(fieldMap).length > 0) {
          map[field.id] = fieldMap;
        }
      });

      state.selectedFilters = map;
    },
  },

  extraReducers: (builder) => {
    // ADD
    builder
      .addCase(AddCustomFields.pending, (state) => {
        state.addingField = true;
        state.errorAddingField = null;
      })
      .addCase(AddCustomFields.fulfilled, (state, action) => {
        state.addingField = false;
        state.addedField = true;

        const newRecord = action.payload?.data ?? action.meta.arg;
        if (newRecord) {
          const tempId = action.meta.arg?.tempId;
          if (tempId) {
            const idx = state.catCustomFields.findIndex((f) => f.id === tempId);
            if (idx !== -1) state.catCustomFields[idx] = newRecord;
            else state.catCustomFields.push(newRecord);
          } else {
            state.catCustomFields.push(newRecord);
          }
        }
      })
      .addCase(AddCustomFields.rejected, (state, action) => {
        state.addingField = false;
        state.errorAddingField = action.payload;
        const tempId = action.meta.arg?.tempId;
        if (tempId) {
          state.catCustomFields = state.catCustomFields.filter(
            (f) => f.id !== tempId,
          );
        }
      });

    // GET
    builder
      .addCase(GetCustomFields.pending, (state) => {
        state.loadingCustomFields = true;
        state.errorCustomFields = null;
        state.catCustomFields = [];
        state.totalQuestions = 0;
      })
      .addCase(GetCustomFields.fulfilled, (state, action) => {
        state.loadingCustomFields = false;
        state.catCustomFields = action.payload?.data ?? [];
        state.totalQuestions = action.payload?.totalItems ?? 0;
        state.errorCustomFields = null;

        state.openQuestions = {};
        state.catCustomFields.forEach((field) => {
          state.openQuestions[field.id] = true;
        });
      })

      .addCase(GetCustomFields.rejected, (state, action) => {
        state.loadingCustomFields = false;
        state.errorCustomFields = action.payload;
        state.catCustomFields = [];
        state.totalQuestions = 0;
      });

    // DELETE
    builder
      .addCase(deleteField.pending, (state, action) => {
        const { id } = action.meta.arg;
        state.catCustomFields = state.catCustomFields.filter(
          (f) => f.id !== id,
        );
        delete state.selectedFilters[id];
        delete state.openQuestions[id];
      })
      .addCase(deleteField.fulfilled, () => {})
      .addCase(deleteField.rejected, () => {
        // Optionally refetch
      });

    // UPDATE
    builder
      .addCase(updateField.pending, (state, action) => {
        state.updatingField = true;
        state.errorUpdatingField = null;
        const { id, data } = action.meta.arg;
        const idx = state.catCustomFields.findIndex((f) => f.id === id);
        if (idx !== -1) {
          state.catCustomFields[idx] = {
            ...state.catCustomFields[idx],
            ...data,
          };
        }
      })
      .addCase(updateField.fulfilled, (state, action) => {
        state.updatingField = false;
        const { id, data } = action.payload;
        const idx = state.catCustomFields.findIndex((f) => f.id === id);
        if (idx !== -1) {
          state.catCustomFields[idx] = data.data || data;
        }
      })
      .addCase(updateField.rejected, (state, action) => {
        state.updatingField = false;
        state.errorUpdatingField = action.payload?.error;
        const { id, oldData } = action.payload || {};
        if (id && oldData) {
          const idx = state.catCustomFields.findIndex((f) => f.id === id);
          if (idx !== -1) state.catCustomFields[idx] = oldData;
        }
      });
  },
});

// ──────────────────────────────────────────────────────────────
//  ACTIONS EXPORT
// ──────────────────────────────────────────────────────────────
export const {
  setCatcustomFields,
  clearBusinessFilters,
  clearFilterSelectionsOnly,
  toggleQuestionOpen,
  toggleFilterOption,
  clearFilterSelections,
  restoreFilterSelections,
} = CustomFieldsSlice.actions;

export default CustomFieldsSlice.reducer;
